import { type FC, useState } from "react"
import { Navigate, useParams } from "react-router"

import { parseNumber } from "@forento/shared/utilities/number"
import { exhaustiveGuard } from "@forento/shared/utilities/switch"

import { type PaypalOrder, PurchaseSuccessModalContextProvider } from "~/contexts/PurchaseSuccessModalContext"
import Button from "~/themes/original/components/Button"
import Hero, { CallToActionButtons, CallToActionInfo } from "~/themes/original/components/Hero"
import Layout from "~/themes/original/components/Layout"
import Modal, { ModalButtons, ModalText, ModalTitle } from "~/themes/original/components/Modal"
import PartialLoadingPage from "~/themes/original/components/PartialLoadingPage"
import PurchaseItem from "~/themes/original/components/PurchaseItem"
import routes from "~/utilities/routes"
import { query } from "~/utilities/trpc"

const SessionPage: FC = () => {
	const sessionId = parseNumber(useParams().sessionId!)

	const [isModalOpen, setModalOpen] = useState(false)
	const [paypalOrder, setPaypalOrder] = useState<PaypalOrder | null>(null)

	const { data: session, error, refetch } = query.privateSession.getPersonalized.useQuery(sessionId ?? -1)

	if (session === null) return <Navigate to={routes.session.index()} />

	const redemptionInstructions = session?.status === "unlocked" ? session.redemptionInstructions : null
	const redemptionLink = session?.status === "unlocked" ? session.redemptionLink : null

	return (
		<PurchaseSuccessModalContextProvider content={{ type: "session" }} paypalOrder={paypalOrder}>
			<Layout>
				<Modal isOpen={isModalOpen}>
					<ModalTitle>Redeem session</ModalTitle>
					<ModalText>{redemptionInstructions}</ModalText>
					<ModalButtons>
						{redemptionLink !== null ? (
							<>
								<Button variant="secondary" onClick={() => setModalOpen(false)}>
									Cancel
								</Button>
								<Button variant="primary" onClick={redemptionLink} newTab>
									Redeem
								</Button>
							</>
						) : (
							<Button variant="primary" onClick={() => setModalOpen(false)}>
								Close
							</Button>
						)}
					</ModalButtons>
				</Modal>
				{error ? (
					<p>Failed to load session.</p>
				) : session === undefined ? (
					<PartialLoadingPage />
				) : (
					<Hero
						title={session.title}
						htmlDescription={session.longDescription?.html ?? null}
						thumbnailUrl={session.thumbnailFilePath}
						callToAction={
							session.status === "paywall" ? (
								<PurchaseItem
									item={{
										type: "session",
										id: session.id,
										name: session.title,
										shortDescription: session.shortDescription,
										price: session.price,
										accessTimeMonths: null,
									}}
									onPurchase={async ({ paypalOrder }) => {
										setPaypalOrder(paypalOrder)
										await refetch()
									}}
								/>
							) : session.status === "sign-in-required" ? (
								<>
									<CallToActionInfo>
										This session is only available to signed in users.
									</CallToActionInfo>
									<CallToActionButtons>
										<Button variant="primary" onClick={routes.user.signin()}>
											Sign in
										</Button>
										<Button variant="primary" onClick={routes.user.signup()}>
											Sign up
										</Button>
									</CallToActionButtons>
								</>
							) : session.status === "membership-required" ? (
								<>
									<CallToActionInfo>This session is only available to members.</CallToActionInfo>
									<CallToActionButtons>
										<Button variant="primary" onClick={routes.membership.index()}>
											Memberships
										</Button>
									</CallToActionButtons>
								</>
							) : session.status === "paused" ? (
								<CallToActionInfo>Sales are paused.</CallToActionInfo>
							) : session.status === "unlocked" ? (
								session.redemptionInstructions !== null ? (
									<Button variant="primary" onClick={() => setModalOpen(true)}>
										Redeem
									</Button>
								) : session.redemptionLink !== null ? (
									<Button variant="primary" onClick={session.redemptionLink} newTab>
										Redeem
									</Button>
								) : null
							) : (
								exhaustiveGuard(session.status)
							)
						}
					/>
				)}
			</Layout>
		</PurchaseSuccessModalContextProvider>
	)
}

export default SessionPage
